import JSZip from "jszip";
import { construirFormDataVisita } from "../services/visitsheets.service";

// ---------------------------------------------------------------------------
// Backup de hojas de visita
// ---------------------------------------------------------------------------
// Cuando una hoja de visita no se puede enviar (red / servidor), el técnico
// puede DESCARGAR un backup: un .zip con TODO lo necesario para (a) reimportar
// la visita y reintentarla en cualquier dispositivo y (b) que devs/soporte
// diagnostiquen el problema. No se sube a ningún sitio: el técnico nos lo pasa
// a mano (WhatsApp, correo…) y/o lo reimporta desde "Cola de visitas".
//
// Contenido del ZIP:
//   - visita.json      → los datos crudos de la visita (para reimportar tal cual)
//   - manifest.json    → mapa de imágenes (principal + adjuntas, en orden)
//   - imgs/principal/  → imagen principal, bytes EXACTOS
//   - imgs/adjuntas/   → imágenes de riesgo, bytes EXACTOS y en orden
//   - form.json        → FormData legible que se intentaba enviar (inspección)
//   - raw-multipart.bin→ cuerpo multipart exacto del navegador (inspección)
//   - meta.json        → dispositivo, usuario, red y detalle del fallo
//   - RESUMEN.txt      → resumen legible de todo lo anterior
// ---------------------------------------------------------------------------

const infoRed = () => {
  const c =
    navigator.connection ||
    navigator.mozConnection ||
    navigator.webkitConnection ||
    null;
  return {
    online: navigator.onLine,
    effectiveType: c?.effectiveType ?? null,
    downlink: c?.downlink ?? null,
    rtt: c?.rtt ?? null,
    saveData: c?.saveData ?? null,
  };
};

const formatearMB = (bytes) => `${((bytes || 0) / (1024 * 1024)).toFixed(2)} MB`;

const sanear = (v) =>
  String(v ?? "")
    .replace(/[^a-zA-Z0-9_.\-]/g, "_")
    .slice(0, 80) || "img";

// Resumen legible (lo que necesita soporte al recibir el ZIP a mano).
const construirResumen = (meta, totalBytes) => {
  const f = meta.fallo || {};
  const r = meta.red || {};
  const d = meta.dispositivo || {};
  return [
    "BACKUP HOJA DE VISITA",
    "=====================",
    `Generado:     ${meta.generadoEn}`,
    `Comunidad:    ${meta.comunidad || "—"} (NIF ${meta.nif || "—"})`,
    `Usuario:      ${meta.usuario?.nombre || "—"} (id ${meta.usuario?.id ?? "—"})`,
    `Fallo:        ${f.tipo || "—"} ${f.code ?? ""} - ${f.error?.message || "—"}`,
    `Reintentos:   attempts=${meta.reintentos?.attempts ?? 0} server=${meta.reintentos?.serverAttempts ?? 0} client=${meta.reintentos?.clientAttempts ?? 0}`,
    `Red:          effectiveType=${r.effectiveType ?? "?"} downlink=${r.downlink ?? "?"} rtt=${r.rtt ?? "?"} online=${r.online}`,
    `Dispositivo:  ${d.userAgent || "—"}`,
    `Plataforma:   ${d.plataforma || "—"} | idioma ${d.idioma || "—"}`,
    `Imágenes:     ${meta.imagenes?.length ?? 0} (total ${formatearMB(totalBytes)})`,
    `raw-multipart: capturado=${meta.rawMultipart?.capturado} size=${formatearMB(meta.rawMultipart?.size)}`,
    "",
    "Este .zip contiene una hoja de visita que NO se pudo enviar.",
    "Se puede reimportar desde la app (Cola de visitas → Importar backup)",
    "y/o enviarse a soporte.plataforma@grupolae.net para diagnóstico.",
  ].join("\n");
};

// Construye el .zip de backup a partir de la entrada de la cola/formulario.
// Devuelve { blob, filename, meta }.
// `opts.incluirRawMultipart` (default false): añade `raw-multipart.bin` con el
// cuerpo multipart exacto. Lo omitimos por defecto porque duplica el peso de
// las imágenes y en iOS Safari los zips grandes rompen `navigator.share` y
// caen a un fallback de descarga que a veces no dispara. El back nunca lo
// consulta — sólo `form.json` + `manifest.json` + `imgs/`.
export const construirBackupBlob = async (entry, resultado, opts = {}) => {
  const { incluirRawMultipart = false } = opts;
  const zip = new JSZip();

  // 1) Datos crudos de la visita → permiten reimportarla tal cual.
  zip.file("visita.json", JSON.stringify(entry?.datos ?? {}, null, 2));

  // 2) Imágenes con sus bytes EXACTOS + manifiesto para reconstruirlas.
  const manifest = {
    id: entry?.id ?? null,
    params: entry?.params ?? {},
    mainImage: null,
    images: [],
  };
  const detalleImgs = [];
  let totalBytes = 0;

  const mainImg = entry?.mainImage || null;
  if (mainImg) {
    const nombre = sanear(mainImg.name || "principal.webp");
    const path = `imgs/principal/${nombre}`;
    zip.file(path, mainImg);
    manifest.mainImage = {
      path,
      name: mainImg.name || nombre,
      type: mainImg.type || null,
      size: mainImg.size ?? null,
    };
    detalleImgs.push({ campo: "img", ...manifest.mainImage });
    totalBytes += mainImg.size || 0;
  }

  (entry?.images || []).forEach((file, i) => {
    if (!file) return;
    const nn = String(i).padStart(2, "0");
    const nombre = `${nn}-${sanear(file.name || `adjunta-${i}.webp`)}`;
    const path = `imgs/adjuntas/${nombre}`;
    zip.file(path, file);
    const item = {
      path,
      name: file.name || nombre,
      type: file.type || null,
      size: file.size ?? null,
    };
    manifest.images.push(item);
    detalleImgs.push({ campo: "images", ...item });
    totalBytes += file.size || 0;
  });

  zip.file("manifest.json", JSON.stringify(manifest, null, 2));

  // 3) FormData legible + cuerpo multipart exacto (para inspección de devs).
  let rawInfo = { capturado: false, motivo: null, size: null };
  try {
    const { formData } = construirFormDataVisita(
      entry.datos,
      entry.images || [],
      entry.mainImage
    );

    const form = {};
    for (const [k, v] of formData.entries()) {
      form[k] =
        typeof v === "string"
          ? v
          : { __file: true, name: v?.name ?? null, type: v?.type ?? null, size: v?.size ?? null };
    }
    zip.file("form.json", JSON.stringify(form, null, 2));

    if (incluirRawMultipart) {
      try {
        const req = new Request("backup-capture", { method: "POST", body: formData });
        const buf = await req.arrayBuffer();
        zip.file("raw-multipart.bin", buf);
        rawInfo = { capturado: true, motivo: null, size: buf.byteLength };
      } catch (e) {
        rawInfo = { capturado: false, motivo: String(e), size: null };
      }
    } else {
      rawInfo = { capturado: false, motivo: "omitido-para-reducir-tamano", size: null };
    }
  } catch (e) {
    rawInfo = { capturado: false, motivo: `construirFormData: ${String(e)}`, size: null };
  }

  // 4) Metadatos.
  const meta = {
    generadoEn: new Date().toISOString(),
    entryId: entry?.id ?? null,
    comunidad: entry?.comunidad ?? entry?.datos?.comunidad ?? null,
    nif: entry?.params?.nif ?? null,
    usuario: { id: entry?.userId ?? null, nombre: entry?.userName ?? null },
    reintentos: {
      attempts: entry?.attempts ?? 0,
      serverAttempts: entry?.serverAttempts ?? 0,
      clientAttempts: entry?.clientAttempts ?? 0,
    },
    fallo: {
      tipo: resultado?.tipo ?? null,
      code: resultado?.code ?? null,
      error: resultado?.error ?? null,
    },
    dispositivo: {
      userAgent: navigator.userAgent,
      plataforma: navigator.platform,
      idioma: navigator.language,
    },
    red: infoRed(),
    imagenes: detalleImgs,
    totalBytesImagenes: totalBytes,
    rawMultipart: rawInfo,
  };
  zip.file("meta.json", JSON.stringify(meta, null, 2));
  zip.file("RESUMEN.txt", construirResumen(meta, totalBytes));

  const safeNif = String(entry?.params?.nif || "sin-nif").replace(/[^a-zA-Z0-9_\-]/g, "") || "sin-nif";
  const filename = `visita-${safeNif}.zip`;

  const blob = await zip.generateAsync({ type: "blob" });
  return { blob, filename, meta };
};

// Descarga/comparte el ZIP en el propio dispositivo. DEBE llamarse desde un gesto
// del usuario (click) y con el blob YA construido: iOS Safari bloquea share y
// descargas si hay un await entre el gesto y la llamada. Por eso el blob se
// prepara antes y aquí sólo se comparte/descarga (sin await previo a share).
// Estrategia iOS: `navigator.share` es la vía preferida en Safari (respeta la
// hoja de compartir con Files, WhatsApp, Mail, AirDrop). Si canShare da false
// o share falla, abrimos el blob en una pestaña nueva — en iOS Safari el link
// download con `a.download` sobre un blob NO descarga: navega o se ignora, y
// window.open sí muestra el zip para que el usuario haga "Guardar en Archivos".
// En resto de navegadores usamos `a.download` que sí descarga directo.
const detectarIOS = () => {
  const ua = navigator.userAgent || "";
  const iosClasico = /iPad|iPhone|iPod/.test(ua);
  // iPadOS 13+ se identifica como Mac con touch; los diferenciamos por maxTouchPoints.
  const iPadOsModerno =
    ua.includes("Mac") && typeof navigator.maxTouchPoints === "number" && navigator.maxTouchPoints > 1;
  return iosClasico || iPadOsModerno;
};

export const descargarBlobLocal = async (blob, filename) => {
  const esIOS = detectarIOS();

  // 1) Web Share API con ficheros (iOS Safari, Android Chrome…).
  try {
    const file = new File([blob], filename, { type: "application/zip" });
    if (
      typeof navigator.canShare === "function" &&
      navigator.canShare({ files: [file] })
    ) {
      try {
        await navigator.share({
          files: [file],
          title: "Backup hoja de visita",
          text: "Hoja de visita que no se pudo enviar. Reimpórtala en la app o envíala a soporte.plataforma@grupolae.net.",
        });
        return true;
      } catch (e) {
        // AbortError = el usuario canceló la hoja de compartir: no forzamos descarga.
        if (e && e.name === "AbortError") return true;
        // Otro error → caemos al fallback (window.open en iOS, link en el resto).
      }
    }
  } catch (e) {
    /* File/share no disponible → fallback */
  }

  // 2) Fallback: iOS necesita window.open (a.download no descarga sobre blob);
  //    en el resto usamos el link download tradicional.
  const url = URL.createObjectURL(blob);

  if (esIOS) {
    try {
      // La pestaña se abre en el mismo gesto del usuario; iOS deja al usuario
      // guardar el fichero desde la vista previa.
      const abierto = window.open(url, "_blank");
      if (!abierto) {
        // Bloqueado por el navegador: último recurso, navegar a la URL. El
        // usuario podrá compartirla / guardarla desde el visor de Safari.
        window.location.href = url;
      }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      return true;
    } catch (e) {
      URL.revokeObjectURL(url);
      return false;
    }
  }

  try {
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 60000);
    return true;
  } catch (e) {
    URL.revokeObjectURL(url);
    return false;
  }
};

// Reconstruye una entrada de cola a partir de un .zip de backup para reencolarla.
// Las imágenes se recuperan con sus bytes EXACTOS (no se recomprimen).
// Devuelve el objeto listo para pasar a enqueue().
export const importarBackup = async (file) => {
  const zip = await JSZip.loadAsync(file);

  const visitaFile = zip.file("visita.json");
  if (!visitaFile) {
    throw new Error("El archivo no es un backup válido de hoja de visita (falta visita.json).");
  }
  const datos = JSON.parse(await visitaFile.async("string"));

  let manifest = { params: {}, mainImage: null, images: [] };
  const mf = zip.file("manifest.json");
  if (mf) {
    try {
      manifest = JSON.parse(await mf.async("string"));
    } catch (e) {
      /* manifiesto ilegible: seguimos con lo que haya */
    }
  }

  // Imagen principal.
  let mainImage = null;
  if (manifest.mainImage?.path) {
    const f = zip.file(manifest.mainImage.path);
    if (f) {
      const blob = await f.async("blob");
      mainImage = new File(
        [blob],
        manifest.mainImage.name || "principal.webp",
        { type: manifest.mainImage.type || blob.type || "image/webp" }
      );
    }
  }

  // Imágenes adjuntas (en orden).
  const images = [];
  for (const im of manifest.images || []) {
    if (!im?.path) continue;
    const f = zip.file(im.path);
    if (!f) continue;
    // eslint-disable-next-line no-await-in-loop
    const blob = await f.async("blob");
    images.push(
      new File([blob], im.name || "adjunta.webp", {
        type: im.type || blob.type || "image/webp",
      })
    );
  }

  const params = manifest.params || {};
  const id =
    manifest.id ||
    (params.nif && params.id
      ? `hojaVisita_${params.nif}_${params.id}${params.idCentroTrabajo ? `_${params.idCentroTrabajo}` : ""}`
      : `import-${params.nif || "sin-nif"}-${Date.now()}`);

  return {
    id,
    type: "create",
    params,
    datos,
    mainImage,
    images,
    comunidad: datos?.comunidad ?? null,
    tecnicoPRL: datos?.tecnicoPRL ?? null,
  };
};
